import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Loader2, ArrowUpRight, Camera, X, Share2 } from "lucide-react";
import { waLink } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CROPS = ["Soya", "Sorgo y maíz", "Caña", "Girasol", "Otro"];

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const inputCls =
  "w-full bg-white/[0.04] border border-white/15 rounded-sm px-4 py-4 text-base text-[#F6F7F5] placeholder:text-[#F6F7F5]/35 focus:outline-none focus:border-[#00A651] focus:shadow-[0_0_20px_rgba(0,166,81,0.2)] transition-all duration-300";

const IASection = () => {
  const [cultivo, setCultivo] = useState("Soya");
  const [ubicacion, setUbicacion] = useState("");
  const [problema, setProblema] = useState("");
  const [foto, setFoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [done, setDone] = useState(false);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, 1024 / Math.max(img.width, img.height));
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        setFoto(canvas.toDataURL("image/jpeg", 0.72));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if ((!problema.trim() && !foto) || loading) return;
    setLoading(true);
    setResult("");
    setDone(false);
    try {
      const res = await fetch(`${API}/ia/orientacion`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cultivo, ubicacion, problema: problema.trim() || "Analizá la foto adjunta.", imagen: foto }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done: finished, value } = await reader.read();
        if (finished) break;
        acc += decoder.decode(value, { stream: true });
        setResult(acc);
      }
      setDone(true);
    } catch {
      setResult("No pudimos generar la orientación ahora. Escribinos por WhatsApp y te ayudamos directo.");
      setDone(true);
    } finally {
      setLoading(false);
    }
  };

  const waMsg = `Hola Fumiga, le conté a la IA mi problema de ${cultivo}${ubicacion ? ` en ${ubicacion}` : ""}: ${problema.slice(0, 140)}`;
  const shareMsg = `Mirá esta herramienta de Fumiga: le contás a la IA qué le pasa a tu cultivo (podés subir foto de la hoja) y te orienta gratis en segundos → ${typeof window !== "undefined" ? window.location.origin : ""}`;

  return (
    <section id="ia" data-testid="ia-section" className="relative bg-[#151A1F] py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 hairlines" aria-hidden="true" />
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 font-wordmark font-bold text-[38vw] leading-none text-[#00A651]/[0.04] select-none pointer-events-none"
        aria-hidden="true"
      >
        IA
      </div>
      <div className="relative mx-auto max-w-7xl px-6 sm:px-10">
        <motion.p {...reveal} className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#00A651]">
          Diagnóstico con IA
        </motion.p>
        <motion.h2
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.1 }}
          className="font-display font-bold tracking-tight leading-[1.08] text-2xl sm:text-4xl lg:text-5xl mt-6 max-w-3xl"
        >
          Cuéntale a la IA qué pasa en tu campo.
        </motion.h2>
        <motion.p
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.15 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-[#F6F7F5]/65 leading-relaxed"
        >
          Describe tu problema — o subí una foto de la hoja dañada — y recibe en segundos
          una orientación inicial y el servicio más adecuado para tu caso.
        </motion.p>

        <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.18 }} className="mt-12 relative overflow-hidden rounded-sm border border-white/10">
          <img
            src="/fotos/ia-dron-verde.webp"
            alt="Dron pulverizando a baja altura sobre cultivo verde en hileras"
            className="w-full h-52 sm:h-72 object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#151A1F]/60 to-transparent" aria-hidden="true" />
          <p className="absolute bottom-4 left-4 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#F6F7F5]/85">
            Lectura de lote georreferenciada · Pulverización activa
          </p>
        </motion.div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.2 }}
            className="border border-white/10 bg-white/[0.03] p-6 sm:p-8 flex flex-col gap-6"
          >
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Tipo de cultivo">
              {CROPS.map((c) => (
                <button
                  key={c}
                  data-testid={`ia-tab-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  role="tab"
                  aria-selected={cultivo === c}
                  onClick={() => setCultivo(c)}
                  className={`font-display font-semibold text-sm px-5 py-2 rounded-full transition-all duration-300 ${
                    cultivo === c
                      ? "bg-[#00A651] text-[#151A1F]"
                      : "border border-white/15 text-[#F6F7F5]/70 hover:border-[#00A651]/50"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <label className="block">
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#F6F7F5]/50">
                Ubicación del predio (opcional)
              </span>
              <input
                data-testid="ia-input-ubicacion"
                type="text"
                value={ubicacion}
                onChange={(e) => setUbicacion(e.target.value)}
                placeholder="Ej. Zona Norte, Montero"
                className={`${inputCls} mt-2`}
              />
            </label>

            <label className="block">
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#F6F7F5]/50">
                Describe tu problema
              </span>
              <textarea
                data-testid="ia-textarea-problema"
                rows={4}
                value={problema}
                onChange={(e) => setProblema(e.target.value)}
                placeholder="Ej. Tengo 40 ha de soya en floración con manchas amarillas que avanzan desde el borde del lote..."
                className={`${inputCls} mt-2 resize-none`}
              />
            </label>

            <div>
              <input
                id="ia-foto-input"
                data-testid="ia-foto-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                onChange={handlePhoto}
                className="hidden"
              />
              {foto ? (
                <div className="flex items-center gap-4">
                  <img src={foto} alt="Foto de la hoja dañada" className="w-16 h-16 object-cover rounded-sm border border-[#00A651]/40" />
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#00A651] flex-1">Foto lista para analizar</p>
                  <button
                    data-testid="ia-foto-remove"
                    onClick={() => setFoto(null)}
                    className="text-[#F6F7F5]/50 hover:text-[#F6F7F5] transition-colors"
                    aria-label="Quitar foto"
                  >
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="ia-foto-input"
                  data-testid="ia-foto-label"
                  className="flex items-center justify-center gap-3 border border-dashed border-white/20 rounded-sm px-4 py-4 cursor-pointer text-[#F6F7F5]/60 hover:border-[#00A651]/50 hover:text-[#F6F7F5] transition-colors duration-300 font-display font-medium text-sm"
                >
                  <Camera size={18} />
                  Adjuntar foto de la hoja (opcional)
                </label>
              )}
            </div>

            <button
              data-testid="ia-submit-button"
              onClick={submit}
              disabled={loading || (!problema.trim() && !foto)}
              className="w-full bg-[#FFB000] text-[#151A1F] font-display font-semibold text-base px-8 py-4 rounded-full hover:scale-[1.01] transition-transform duration-300 disabled:opacity-60 disabled:hover:scale-100 inline-flex items-center justify-center gap-3"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
              {loading ? "Analizando tu campo..." : "Obtener orientación inicial"}
            </button>

            <p className="font-mono-tech text-[10px] uppercase tracking-[0.15em] text-[#F6F7F5]/35 leading-relaxed">
              Orientación generada con inteligencia artificial. No reemplaza una evaluación en campo ni una recomendación profesional.
            </p>
          </motion.div>

          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.25 }}
            data-testid="ia-result-panel"
            className="border border-[#00A651]/20 bg-[#0E1512] p-6 sm:p-8 min-h-[320px] flex flex-col"
          >
            {!result && !loading && (
              <div className="flex-1 flex flex-col items-center justify-center text-center gap-6">
                <Sparkles size={28} className="text-[#00A651]" />
                <p className="text-[#F6F7F5]/55 text-sm sm:text-base leading-relaxed max-w-xs">
                  Describe lo que está pasando en tu campo — con texto o con una foto de la
                  hoja — y recibirás una primera lectura técnica.
                </p>
              </div>
            )}
            {(result || loading) && (
              <>
                <div className="flex items-start justify-between gap-4">
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#00A651]">
                    Orientación inicial · {cultivo}
                  </p>
                  {foto && <img src={foto} alt="Hoja analizada" className="w-12 h-12 object-cover rounded-sm border border-white/15" />}
                </div>
                <div className="mt-5 flex-1 text-[#F6F7F5]/85 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                  {result.replace(/\*\*/g, "")}
                  {loading && <span className="inline-block w-2 h-4 bg-[#00A651] ml-1 animate-pulse align-middle" />}
                </div>
                {done && (
                  <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center gap-4">
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#F6F7F5]/45 w-full">
                      Siguiente paso: evaluación en campo
                    </p>
                    <a
                      data-testid="ia-whatsapp-cta"
                      href={waLink(waMsg)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FFB000] text-[#151A1F] font-display font-semibold text-sm px-6 py-3 rounded-full hover:scale-[1.03] transition-transform duration-300"
                    >
                      Cotizar por WhatsApp <ArrowUpRight size={16} />
                    </a>
                    <a
                      data-testid="ia-share-button"
                      href={waLink(shareMsg)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 border border-white/20 text-[#F6F7F5]/80 font-display font-medium text-sm px-6 py-3 rounded-full hover:border-[#00A651] hover:text-[#00A651] transition-colors duration-300"
                    >
                      <Share2 size={16} /> Compartir herramienta
                    </a>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default IASection;
